import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import YoutubePlayer from '../components/YoutubePlayer';
import RoundNavigation from '../components/RoundNavigation';
import Timeline from '../components/Timeline';
import Notes from '../components/Notes';
import styles from './Match.module.css';
import { fetchAPI } from '../utils/api';
import { postUsageData } from '../utils/usage';
import { DetailedRoundData, MapData, MatchData, RoundData } from '../utils/types';
import MapNavigation from '../components/MapNavigation';
import RoundNavigationPopup from '../components/RoundNavigationPopup';
import { Button } from 'react-bootstrap';
import MapNavigationPopup from '../components/MapNavigationPopup';
import AudioVolume from '../components/AudioVolume';
import HotkeysLabel from '../components/HotkeysLabel';
import JSZip from 'jszip';
import { processAbilityUsage } from '../utils/abilities';
import { AbilityUse } from '../utils/types';
import UtilityTable from '../components/UtilityTable';
import Settings from '../components/Settings';
import { useAuthState } from '../utils/auth';
import { postMatchUsageData } from '../utils/usage';

enum PlayerState {
  UNSTARTED = -1,
  ENDED = 0,
  PLAYING = 1,
  PAUSED = 2,
  BUFFERING = 3,
  CUED = 5
}

const Match: React.FC = () => {
  const { match_id, map_index, round_number } = useParams<{ match_id: string; map_index?: string; round_number?: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Add function to check if match is 30 days or older
  const isMatchOlderThan30Days = (matchId: string): boolean => {
    const matchDate = new Date(matchId.substring(0, 10));
    const currentDate = new Date();
    const diffTime = Math.abs(currentDate.getTime() - matchDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays >= 30;
  };

  // Redirect if map_index is not provided
  useEffect(() => {
    if (!map_index) {
      navigate(`/match/${match_id}/1`, { replace: true });
    }
  }, [match_id, map_index, navigate]);

  const [match, setMatch] = useState<MatchData | null>(null);
  const [rounds, setRounds] = useState<RoundData[]>([]);
  const [currentRound, setCurrentRound] = useState<number>(parseInt(round_number || '1'));
  const [targetTime, setTargetTime] = useState<number | undefined>(undefined);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isSliderDragging, setIsSliderDragging] = useState(false);
  const [isNarrowScreen, setIsNarrowScreen] = useState(false);
  const [isRoundPopupOpen, setIsRoundPopupOpen] = useState(false);
  const [isMapPopupOpen, setIsMapPopupOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [volume, setVolume] = useState(50); // Add this new state
  const [visualTime, setVisualTime] = useState(0);
  const timeoutRef = useRef<NodeJS.Timeout>();
  const [playerState, setPlayerState] = useState<PlayerState>(PlayerState.UNSTARTED);
  const [previousVolume, setPreviousVolume] = useState<number | null>(null);
  const [isMinimapZoomed, setIsMinimapZoomed] = useState(false);
  const [roundsDetailedData, setRoundsDetailedData] = useState<Record<number, DetailedRoundData>>({});
  const [isUtilityTableOpen, setIsUtilityTableOpen] = useState(false);
  const [selectedPlayerIndices, setSelectedPlayerIndices] = useState<number[]>([0]);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  
  // Use the custom auth hook
  const { authState, hasSubscription } = useAuthState();
  
  const [videoQuality, setVideoQuality] = useState(() => {
    return localStorage.getItem('video_quality') || '1080p';
  });

  // Get controls from query params and localStorage
  const searchParams = new URLSearchParams(location.search);
  const queryControls = searchParams.get('controls') === '1';
  const storedControls = localStorage.getItem('youtube_controls') === 'visible';
  const showYTControls = queryControls || storedControls;

  const createMissingRounds = (existingRounds: RoundData[], roundCount: number): RoundData[] => {
    const processedRounds: RoundData[] = [];
    for (let i = 1; i <= roundCount; i++) {
      const existingRound = existingRounds.find(r => r.round_number === i);
      if (existingRound) {
        processedRounds.push({
          ...existingRound,
          enabled: true
        });
      } else {
        processedRounds.push({
          round_number: i,
          round_start: 0,
          round_end: 0,
          enabled: false
        });
      }
    }
    return processedRounds;
  };

  const usageLogged = useRef(false);

  useEffect(() => {
    if (!map_index) {
      navigate(`/match/${match_id}/1`, { replace: true });
    } else {
      // Fetch match and rounds data
      const fetchData = async () => {
        setIsLoading(true);
        try {
          const [matchData, roundsData] = await Promise.all([
            fetchAPI(`/matches/${match_id}`),
            fetchAPI(`/rounds/${match_id}/${map_index}`)
          ]);

          setMatch(matchData as MatchData);

          const mapData = matchData.maps[parseInt(map_index) - 1] as MapData;
          const round_count = mapData.teamA_score + mapData.teamB_score;
          const existingRounds = roundsData.rounds as RoundData[];
          const processedRounds = createMissingRounds(existingRounds, round_count);
          setRounds(processedRounds);

          // Fetch detailed round data for enabled rounds
          const detailedData: Record<number, DetailedRoundData> = {};
          
          // First, get all signed URLs in a single batch request
          let roundUrls: Array<{round_number: number, url: string}> = [];
          try {
            const batchResponse = await fetchAPI(`/round/${match_id}/${map_index}`);
            if (batchResponse.success && batchResponse.urls) {
              // Filter to only include enabled rounds
              const enabledRoundNumbers = new Set(processedRounds.filter(round => round.enabled).map(round => round.round_number));
              roundUrls = batchResponse.urls.filter((urlData: {round_number: number, url: string}) => 
                enabledRoundNumbers.has(urlData.round_number)
              );
            }
          } catch (error) {
            console.error('Error fetching batch round URLs:', error);
            // Fall back to individual requests if batch fails
            roundUrls = [];
            for (const round of processedRounds.filter(round => round.enabled)) {
              try {
                const response = await fetchAPI(`/round/${match_id}/${map_index}/${round.round_number}`);
                if (response.success && response.url) {
                  roundUrls.push({ round_number: round.round_number, url: response.url });
                }
              } catch (individualError) {
                console.error(`Error fetching individual round ${round.round_number}:`, individualError);
              }
            }
          }

          // Now download and process all the zip files asynchronously
          await Promise.all(
            roundUrls.map(async ({ round_number, url }) => {
              try {
                const zipResponse = await fetch(url);
                const zipBlob = await zipResponse.blob();
                const zip = await JSZip.loadAsync(zipBlob);
                const roundFile = zip.file('round.json');
                if (roundFile) {
                  const jsonStr = await roundFile.async('string');
                  const roundData = JSON.parse(jsonStr);

                  // Process ability usage for all players
                  const abilityUsage: Record<number, AbilityUse[]> = {};
                  for (let playerIndex = 0; playerIndex < roundData.frames[0].players.length; playerIndex++) {
                    abilityUsage[playerIndex] = processAbilityUsage(roundData.frames, playerIndex, false);
                  }
                  roundData.abilityUsage = abilityUsage;

                  detailedData[round_number] = roundData;
                }
              } catch (error) {
                console.error(`Error processing detailed data for round ${round_number}:`, error);
              }
            })
          );

          setRoundsDetailedData(detailedData);

          // Post usage data for match page visit only once per match
          if (!usageLogged.current) {
            postUsageData('match').catch(error => {
              console.error('Failed to post match usage data:', error);
            });
            usageLogged.current = true;
          }
        } catch (error) {
          console.error('Error fetching match data:', error);
          // Handle error (e.g., show error message to user)
        } finally {
          setIsLoading(false);
        }
      };

      fetchData();
    }
  }, [match_id, map_index, navigate]);

  // Reset usageLogged when match_id changes
  useEffect(() => {
    usageLogged.current = false;
  }, [match_id]);

  // Redirect if trying to access an unprocessed map
  useEffect(() => {
    if (match && map_index && !isLoading) {
      const currentMapIndex = parseInt(map_index);
      const currentMapData = match.maps[currentMapIndex - 1];
      
      if (currentMapData && !match.processed_map_names.includes(currentMapData.map_name)) {
        // Find first processed map and redirect there
        const firstProcessedMapIndex = match.maps.findIndex(map => 
          match.processed_map_names.includes(map.map_name)
        );
        
        if (firstProcessedMapIndex !== -1) {
          navigate(`/match/${match_id}/${firstProcessedMapIndex + 1}`, { replace: true });
        }
      }
    }
  }, [match, map_index, isLoading, navigate, match_id]);



  const handleRoundSelect = (round: number, preserveTimeOffset: boolean = false) => {
    if (!rounds.length) return;

    // Check if round is restricted and user doesn't have subscription
    // Allow access if match is 30 days or older
    if (round > 12 && !hasSubscription && !isMatchOlderThan30Days(match_id || '')) {
      return;
    }

    setCurrentRound(round);
    setIsPlaying(true);
    const roundData = rounds.find(r => r.round_number === round);
    
    // Only get time offset from URL params if preserveTimeOffset is true
    const params = new URLSearchParams(location.search);
    const timeOffset = preserveTimeOffset ? params.get('t') : null;

    if (roundData) {
      if (timeOffset) {
        const offsetSeconds = parseInt(timeOffset);
        const targetTimeWithOffset = roundData.round_start + offsetSeconds;
        setVisualTime(targetTimeWithOffset);
        setTargetTime(targetTimeWithOffset);
      } else {
        setVisualTime(roundData.round_start);
        setTargetTime(roundData.round_start);
      }
    }
    
    if (!round_number || parseInt(round_number) !== round) {
      // Update URL without triggering navigation side effects
      // Clear the time parameter when manually navigating rounds
      const newUrl = `/match/${match_id}/${map_index}/${round}`;
      window.history.replaceState(null, '', newUrl);
    }

    // Post match usage data
    postMatchUsageData(match_id || '', map_index || '', round.toString()).catch(error => {
      console.error('Failed to post match usage data:', error);
    });
  };

  const handleMapChange = (mapName: string) => {
    if (match) {
      const mapIndex = match.maps.findIndex(map => map.map_name === mapName);
      if (mapIndex !== -1 && match.processed_map_names.includes(mapName)) {
        navigate(`/match/${match_id}/${mapIndex + 1}`, { replace: true });
      }
    }
  };

  useEffect(() => {
    if (rounds.length > 0 && !round_number) {
      const firstEnabledRound = rounds.find(round => round.enabled)?.round_number || 1;
      handleRoundSelect(firstEnabledRound, true); // Preserve time offset for initial load
    } else if (round_number) {
      handleRoundSelect(parseInt(round_number), true); // Preserve time offset for initial load
    }
  }, [rounds]);

  const handleTimeUpdate = (newTime: number) => {
    if (isLoading || isSliderDragging || playerState !== PlayerState.PLAYING) return;

    // Only update if there's no pending visual time update
    if (!timeoutRef.current) {
      setVisualTime(newTime);
    }
  };

  const handleRoundTimeChange = (newRoundTime: number) => {
    if (!rounds.length) return;
    const absoluteTime = newRoundTime + rounds[currentRound - 1].round_start;
    setVisualTime(absoluteTime);
    setTargetTime(absoluteTime);
  };

  const handleDraggingChange = (isDragging: boolean) => {
    setIsSliderDragging(isDragging);
  };

  const handlePlayPauseToggle = () => {
    setIsPlaying((currentValue) => !currentValue);
  };

  const getCurrentRound = () => {
    if (!rounds.length) return { round_start: 0, round_end: 0 };
    return rounds[currentRound - 1] || { round_start: 0, round_end: 0 };
  };

  const handleStatsClick = () => {
    if (currentMap && currentMap.stats_url) {
      window.open(currentMap.stats_url, '_blank');
    }
  };

  const handleYoutubeClick = () => {
    if (currentMap && currentMap.youtube_id) {
      const youtubeUrl = `https://www.youtube.com/watch?v=${currentMap.youtube_id}&t=${Math.floor(targetTime || 0)}s`;
      window.open(youtubeUrl, '_blank');
    }
  };

  useEffect(() => {
    const checkScreenSize = () => {
      setIsNarrowScreen(window.innerWidth < 1280); // Adjust this breakpoint as needed
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => {
      window.removeEventListener('resize', checkScreenSize);
    };
  }, []);

  const toggleRoundPopup = () => {
    setIsRoundPopupOpen(!isRoundPopupOpen);
  };

  const toggleMapPopup = () => {
    setIsMapPopupOpen(!isMapPopupOpen);
  };

  const toggleZoom = () => {
    setIsMinimapZoomed(current => !current);
  };

  const JumpToTime = (newTime: number) => {
    // Update visual time immediately
    setVisualTime(newTime);
    setPlayerState(PlayerState.BUFFERING);
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    // Set a timeout to update the actual time
    timeoutRef.current = setTimeout(() => {
      setTargetTime(newTime);
      timeoutRef.current = undefined;
    }, 100);
  };

  // Add keyboard controls
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      
      // For 'n' key, allow it unless we're already in the notes textarea
      if (event.key && event.key.toLowerCase() === 'n') {
        // Check if we're in the notes textarea specifically
        const isInNotesTextarea = target && target.classList.contains('editTextarea');
        if (!isInNotesTextarea) {
          event.preventDefault();
          setIsPlaying(false); // Pause playback
          // Dispatch custom event to focus notes textarea
          const focusNotesEvent = new CustomEvent('focusNotesTextarea');
          window.dispatchEvent(focusNotesEvent);
          return;
        }
      }

      // Ignore other keyboard controls if user is typing in an input field
      if (target && (
        target.tagName === 'INPUT' || 
        target.tagName === 'TEXTAREA' || 
        target.contentEditable === 'true' ||
        target.isContentEditable
      )) {
        return;
      }

      switch (event.key) {
        case ' ': // Spacebar
          event.preventDefault();
          handlePlayPauseToggle();
          break;
        case 'ArrowLeft':
          if (event.shiftKey) {
            // Previous round
            const prevRound = Math.max(1, currentRound - 1);
            if (rounds[prevRound - 1]?.enabled) {
              handleRoundSelect(prevRound); // Don't preserve time offset for manual navigation
            }
          } else {
            // Skip 5s backward
            const newTime = Math.max(0, visualTime - 5);
            JumpToTime(newTime);
          }
          break;
        case 'ArrowRight':
          if (event.shiftKey) {
            // Next round
            const nextRound = Math.min(rounds.length, currentRound + 1);
            if (rounds[nextRound - 1]?.enabled) {
              handleRoundSelect(nextRound); // Don't preserve time offset for manual navigation
            }
          } else {
            // Skip 5s forward
            const newTime = visualTime + 5;
            JumpToTime(newTime);
          }
          break;
        case 'm': // Add mute/unmute hotkey
          if (previousVolume === null) {
            setPreviousVolume(volume);
            handleVolumeChange(0);
          } else {
            handleVolumeChange(previousVolume);
            setPreviousVolume(null);
          }
          break;
        case 'z': // Add zoom toggle hotkey
          toggleZoom();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentRound, rounds, visualTime, volume, previousVolume, toggleZoom]);

  // Memoize youtube_vods to prevent unnecessary re-renders and API calls
  const youtube_vods = useMemo(() => {
    if (!match) return [];
    const currentMap = match.maps[parseInt(map_index || '1') - 1];
    return [{ 
      youtube_id: currentMap ? currentMap.youtube_id : '', 
      seconds_from_session_start: 0 
    }];
  }, [match, map_index]);

  // Handle note click to navigate to note's time and round
  const handleNoteClick = (globalTime: number, roundNumber?: number, _youtubeId?: string) => {
    if (roundNumber) {
      // Check if round is restricted and user doesn't have subscription
      if (roundNumber > 12 && !hasSubscription && !isMatchOlderThan30Days(match_id || '')) {
        return;
      }

      // Change to the note's round if different from current
      if (roundNumber !== currentRound) {
        setCurrentRound(roundNumber);
        // Update URL without triggering navigation side effects
        const newUrl = `/match/${match_id}/${map_index}/${roundNumber}`;
        window.history.replaceState(null, '', newUrl);
      }
    }

    // Seek to the note's time using JumpToTime for proper handling
    JumpToTime(globalTime);

    // Post match usage data
    if (roundNumber) {
      postMatchUsageData(match_id || '', map_index || '', roundNumber.toString()).catch(error => {
        console.error('Failed to post match usage data:', error);
      });
    }
  };

  if (!match || !rounds.length) {
    return <div style={{ margin: '1rem', color: '#777777', fontSize: '1rem' }}>Loading...</div>;
  }

  const currentMap = match.maps[parseInt(map_index || '1') - 1];

  const handleVolumeChange = (newVolume: number) => {
    setVolume(newVolume);
  };

  const handleUtilityTableCellClick = (roundNumber: number, playerIndex: number, timeOffset?: number) => {
    // Only select the clicked player
    setSelectedPlayerIndices([playerIndex]);
    
    // If there's a time offset, add it to the URL and use handleRoundSelect with preserve flag
    if (timeOffset !== undefined) {
      const timeParam = `?t=${timeOffset}`;
      navigate(`/match/${match_id}/${map_index}/${roundNumber}${timeParam}`, { replace: true });
      // Use setTimeout to ensure the URL is updated before calling handleRoundSelect
      setTimeout(() => {
        handleRoundSelect(roundNumber, true); // Preserve time offset for utility table navigation
      }, 0);
    } else {
      navigate(`/match/${match_id}/${map_index}/${roundNumber}`, { replace: true });
      setTimeout(() => {
        handleRoundSelect(roundNumber, false);
      }, 0);
    }
  };

  const handleUtilityTableOpen = () => {
    setIsPlaying(false);  // Pause playback
    setIsUtilityTableOpen(true);
  };

  const handleUtilityTableClose = () => {
    setIsUtilityTableOpen(false);
    setIsPlaying(true);  // Resume playback
  };

  return (
    <div className={styles.matchPage}>
      <div className={`${styles.videoContainer} ${isNarrowScreen ? styles.mobileVideoContainer : ''}`}>
        <div className={styles.videoWrapper}>
          <YoutubePlayer
            videoId={currentMap ? currentMap.youtube_id : ''}
            targetTime={targetTime}
            isPlaying={isPlaying && !isSliderDragging}
            onTimeUpdate={handleTimeUpdate}
            onUserPlay={() => setIsPlaying(true)}
            onUserPause={() => setIsPlaying(false)}
            volume={volume}
            onPlayerStateChange={setPlayerState}
            minimapZoom={isMinimapZoomed}
            videoQuality={videoQuality as '720p' | '1080p' | '1440p' | '4k'}
            showControls={showYTControls}
          />
        </div>
        {!isNarrowScreen && (
          <Notes 
            youtube_vods={youtube_vods}
            active_youtube_id={currentMap ? currentMap.youtube_id : ''}
            currentTime={visualTime}
            onExpandedChange={() => {}}
            roundInfo={{
              currentRound,
              currentLocalTime: visualTime - getCurrentRound().round_start,
              rounds: rounds.map(round => ({
                round_number: round.round_number,
                round_start: round.round_start,
                round_end: round.round_end
              }))
            }}
            onNoteClick={handleNoteClick}
            authState={authState}
            isPlaying={isPlaying}
            onPlaybackControl={setIsPlaying}
          />
        )}
      </div>
      <div className={styles.controlsContainer}>
        {isNarrowScreen ? (
          <div className={styles.mobileControls}>
            <Timeline
              isPlaying={isPlaying}
              currentRoundTime={visualTime - getCurrentRound().round_start}
              roundDuration={getCurrentRound().round_end - getCurrentRound().round_start}
              roundData={roundsDetailedData[currentRound]}
              onPlayPauseToggle={handlePlayPauseToggle}
              onRoundTimeChange={handleRoundTimeChange}
              onDraggingChange={handleDraggingChange}
              selectedPlayerIndices={selectedPlayerIndices}
              onPlayerSelectionsChange={setSelectedPlayerIndices}
              onUtilityTableOpen={handleUtilityTableOpen}
            />
            <Button
              variant="outline-secondary"
              className={styles.mapToggleButton}
              onClick={toggleMapPopup}
            >
              <span className={styles.roundNumber}>{currentMap.map_name}</span>
            </Button>
            <Button
              variant="outline-secondary"
              className={styles.roundToggleButton}
              onClick={toggleRoundPopup}
            >
              <span className={styles.roundNumber}>#{currentRound}</span>
            </Button>
          </div>
        ) : (
          <>
            <div className={styles.navigationWrapper}>
              <div className={styles.mapNavigationContainer}>
                <MapNavigation
                  maps={match.maps}
                  currentMapIndex={parseInt(map_index || '1')}
                  matchId={match_id || ''}
                  processedMapNames={match.processed_map_names}
                />
                <div>
                  <button
                    className={`${styles.utilitytableButton}`}
                    onClick={handleUtilityTableOpen}
                    title="Utility table"
                  >
                    <i className={`bi bi-table`} />
                  </button>
                  <button
                    className={`${styles.minimapButton}`}
                    onClick={toggleZoom}
                    title="Toggle minimap"
                  >
                    <i className={`bi bi-arrow-${isMinimapZoomed ? 'down-right' : 'up-left'}`} />
                  </button>
                </div>
              </div>
              <div className={styles.roundNavigationContainer}>
                <RoundNavigation
                  currentRound={currentRound}
                  roundsData={rounds}
                  onRoundChange={(round) => handleRoundSelect(round, false)} // Don't preserve time offset for manual navigation
                  onRewindToRoundStart={(round) => {
                    const roundData = rounds.find(r => r.round_number === round);
                    if (roundData) {
                      // Add a small random offset (0.001 to 0.009) to ensure the value changes
                      const smallOffset = Math.random() * 0.009 + 0.001;
                      JumpToTime(roundData.round_start + smallOffset);
                    }
                  }}
                  hasSubscription={hasSubscription}
                  matchId={match_id || ''}
                />
              </div>
              <div className={styles.externalLinksContainer}>
                <HotkeysLabel />
                <button className={`${styles.externalButton}`} onClick={handleStatsClick}>
                  <i className="bi bi-arrow-up-right-square"></i> VLR
                </button>
                <button className={`${styles.externalButton}`} onClick={handleYoutubeClick}>
                  <i className="bi bi-arrow-up-right-square"></i> YT
                </button>
              </div>
            </div>
            <div className={styles.timelineVolumeContainer}>
              <Timeline
                isPlaying={isPlaying}
                currentRoundTime={visualTime - getCurrentRound().round_start}
                roundDuration={getCurrentRound().round_end - getCurrentRound().round_start}
                roundData={roundsDetailedData[currentRound]}
                onPlayPauseToggle={handlePlayPauseToggle}
                onRoundTimeChange={handleRoundTimeChange}
                onDraggingChange={handleDraggingChange}
                selectedPlayerIndices={selectedPlayerIndices}
                onPlayerSelectionsChange={setSelectedPlayerIndices}
                onUtilityTableOpen={handleUtilityTableOpen}
              />
              <AudioVolume volume={volume} onVolumeChange={handleVolumeChange} />
            </div>
          </>
        )}
      </div>
      {isNarrowScreen && isRoundPopupOpen && (
        <RoundNavigationPopup
          currentRound={currentRound}
          roundsData={rounds}
          onRoundChange={(round) => handleRoundSelect(round, false)} // Don't preserve time offset for manual navigation
          onClose={toggleRoundPopup}
          hasSubscription={hasSubscription}
          matchId={match_id || ''}
        />
      )}
      {isNarrowScreen && isMapPopupOpen && (
        <MapNavigationPopup
          maps={match.maps.map(map => map.map_name)}
          currentMapName={currentMap.map_name}
          processedMapNames={match.processed_map_names}
          onMapChange={handleMapChange}
          onClose={toggleMapPopup}
        />
      )}
      <UtilityTable
        isOpen={isUtilityTableOpen}
        onClose={handleUtilityTableClose}
        rounds={rounds}
        roundsDetailedData={roundsDetailedData}
        onCellClick={handleUtilityTableCellClick}
        hasSubscription={hasSubscription || isMatchOlderThan30Days(match_id || '')}
      />
      <Settings isOpen={isSettingsOpen} onClose={() => setIsSettingsOpen(false)} />
    </div>
  );
};

export default Match;
